import { client } from './claims';
import type { TokenMapping } from '../components/caf-workflows/workflowTypes';
import type { CatalogueRef, CatalogueOption, CatalogueReviews, ProviderEmailConfig, ProviderEmailPreview, UsageReference } from './emailCatalogue';

/** AI Curation > Automation (python-backend/app/routes/admin_caf_automation.py). */
const base = '/api/admin/caf-automation';

export type AiMode = 'off' | 'watching' | 'on';
export type WorkflowState = 'draft' | 'watching' | 'on' | 'paused';
/** The three emails a step card can send. */
export type EmailKey = 'loa' | 'multi_rep' | 'cancel_confirmation';
/** Preview items also cover the forward step and a Cancel the claim card's notices (never a card's own key). */
export type PreviewEmailKey = EmailKey | 'forward' | 'cancel_notice' | 'catalogue_email';
/** The Lender task lists (spec 2026-10-08 R3); a "Put on list" card ends on one. */
export type Worklist = 'final_response' | 'provisional' | 'needs_reply' | 'not_sure' | 'needs_checking';
/** Which claim deadline a "Set deadline" card records. */
export type DeadlineKind = 'objection' | 'fos';
export interface PredictedKeyDate { kind: DeadlineKind; label: string; date: string | null; letter_date: string | null; estimated: boolean; source: string | null }
/** What a Watching decision would have recorded; nothing here happened. */
export interface Predictions { key_dates: PredictedKeyDate[]; worklist: { worklist: Worklist; note: string; due_date: string | null } | null }

export interface AiSwitch { mode: AiMode; epoch: number; raw: string | null; legacy: boolean }
export interface SwitchChange { by: string | null; at: string | null; note: string | null }
export interface DayCounts { date: string; auto: number; workflow: number; manual: number }
export interface ByWorkflowRow { workflow_id: string; name: string; state: WorkflowState; used_by: number; counts: number[]; total: number }
export interface SendLimit { slot_limit: number; revision: number; occupied: number }

export type EvidenceScope = 'caf-workflows-1' | 'phase1';
export interface ReplacementSource { kind: string; id: string; label: string; revision: number | string }
export interface FrozenPlan { cards: StepCard[]; ending: string | null; configuration_refs: unknown; conditions: unknown; limitations: unknown }
export interface RunSummary {
  run_id: string; task_id: string; workflow_id: string; name: string | null; version: number;
  source: ReplacementSource | null; state: string; status: string; admission_kind: 'example';
  until: string | null; reason: string | null; ending: string | null; revision: number; paused: boolean;
}
export interface RunDetail {
  run_id: string; task_id: string; workflow_id: string; workflow_version: number; admission_kind: 'example';
  revision: number; status: string; reason_code: string | null; node_id: string | null;
  steps: Array<{ id: string; kind: string }>;
  paused: boolean; response_stopped: boolean;
  next_due_at: string | null; current_version: number | null; active_version: number | null;
  workflow_paused: boolean; workflow: { id: string; name: string; version: number } | null;
  source: ReplacementSource | null; plan: FrozenPlan | null; ending: string | null; attention: boolean;
  allowed_actions: string[]; action_reasons: Record<string, string>;
  events: Array<{ event_id: string; sequence: number; kind: string; created_at: string; node_id: string | null }>;
  effects: Array<{ effect_id: string; node_id: string; status: string }>;
}
export interface ActivationReview {
  revision: number; version: number | null; previous_active_version: number | null;
  watched_versions: number[]; evidence_scope: string;
  changes: Array<{ field: string; before: unknown; after: unknown }>;
  needs_check: boolean; blockers: string[];
}
export interface Overview {
  generated_at: string | null;
  days: number;
  switch: AiSwitch & { changed: SwitchChange | null };
  live: { waiting: number; last_decided_at: string | null };
  workflows_paused: boolean;
  control_revision: number;
  series: DayCounts[];
  totals: { auto: number; workflow: number; manual: number; total: number };
  by_workflow: ByWorkflowRow[];
  by_workflow_more: number;
  matches_staff: { finished: number; matched: number; percent: number | null } | null;
  workflow_count: number;
  workflows_on: number;
  seeds_missing: number;
  send_limit: SendLimit | null;
  last_problem_at: string | null;
  capabilities?: { runs: boolean; blockers?: string[]; policy_version?: string };
  activation?: { waiting_eligible: number; watched_eligible: number };
  evidence?: { scope: EvidenceScope; available_scopes: EvidenceScope[]; observed_versions: Array<{ workflow_id: string; version: number; decisions: number }> };
  execution?: { started: number; completed: number; endings: Record<string, number> } | null;
}

export interface LegacyCoveringEmail { template_id: string; subject: string; tokens: Record<string, TokenMapping> }
export type CoveringEmail = LegacyCoveringEmail | ProviderEmailConfig;

export type StepCard =
  | { kind: 'attach' }
  | { kind: 'note'; text: string }
  | { kind: 'email'; email: EmailKey; catalogue_ref?: CatalogueRef; reminder_catalogue_ref?: CatalogueRef }
  | { kind: 'catalogue_email'; catalogue_ref: CatalogueRef }
  | { kind: 'forward'; covering_email?: CoveringEmail; forwarding_scope?: 'closed_claim_default' }
  | { kind: 'wait'; days: number }
  | { kind: 'cancel_claim'; reason: string; fee: 'usual' | 'none'; catalogue_emails?: Record<string, CatalogueRef> }
  | { kind: 'done' }
  | { kind: 'archive' }
  | { kind: 'set_deadline'; deadline: DeadlineKind }
  | { kind: 'manual'; note: string; worklist?: Worklist };

export interface UsedBy { entry_id: string; sender: string; action?: string | null }
export interface BuiltInDifference { decided_at: string | null; subject: string; claim_reference: string | null; why: 'other_claim' | 'no_send' | 'other_ending' }
export interface BuiltInMatch { same: number; different: number; not_covered: number; percent: number | null; differences: BuiltInDifference[] }

export interface WorkflowView {
  workflow_id: string;
  name: string;
  state: WorkflowState;
  revision: number;
  seed_key: string | null;
  needs_check: boolean;
  pause_note: string | null;
  current_version: number | null;
  active_version: number | null;
  new_version_waiting: boolean;
  read_only: boolean;
  settings_changed: boolean;
  steps: StepCard[] | null;
  checked_templates: Record<string, string>;
  checked_catalogue?: CatalogueReviews;
  definition_revision?: number | null;
  catalogue_needs_review?: boolean;
  catalogue_review?: Array<UsageReference & { context: 'draft' | 'current' | 'active'; version: number | null }>;
  used_by: UsedBy[];
  rule_keys: string[];
  updated_at: string | null;
  updated_by: string | null;
  last_days?: { counts: number[]; total: number };
  built_in?: BuiltInMatch | null;
  followed_last_7_days?: number;
}

export interface WorkflowList {
  items: WorkflowView[];
  switch: AiMode;
  workflows_paused: boolean;
  control_revision: number;
  seeds_missing: number;
  days: number;
}

export interface EmailOption {
  key: EmailKey; name: string; available: boolean; template_id: string | null;
  reminder: boolean; reminder_template_id: string | null;
}
export interface StepOptions { emails: EmailOption[]; forward_template_id: string | null; cancel_reasons: string[]; wait_days: number[];
  /** Present on a server with lists: the "Put on list" choices, in order. */
  worklists?: Array<{ key: Worklist; label: string }>;
  /** Present on a server with the Set deadline card. */
  deadline_kinds?: Array<{ key: DeadlineKind; label: string }>;
  catalogue_options?: CatalogueOption[]; catalogue_next_cursor?: string | null; catalogue_blockers?: string[];
  cancellation_roles?: Record<string, Array<{ role: string; purpose: string; relationship: string; recipient: string }>>;
  forward_excluded_template_ids?: string[];
  forward_token_choices?: Array<{ label: string; token: TokenMapping }>;
  /** Notice template ids each Cancel the claim reason sends (same source publish uses). */
  cancel_templates?: Record<string, string[]>;
}

export interface LegacyPreviewEmail {
  delivery_mode?: undefined;
  template_id: string; source_hash: string; subject: string; html: string; made_up: string[]; recipient: PreviewRecipient;
}
export type PreviewEmail = LegacyPreviewEmail | ProviderEmailPreview;
export type PreviewRecipient = 'client' | 'lender' | 'approved_lender' | 'original_sender';
export type PreviewItem =
  | ({ index: number; email: PreviewEmailKey; status: 'ready'; reminder: PreviewEmail | null;
    review_key?: string; review_hash?: string } & PreviewEmail)
  | (ProviderEmailPreview & { index: number; email: PreviewEmailKey; reminder: PreviewEmail | null })
  | { delivery_mode?: undefined; index: number; email: PreviewEmailKey; status: 'not_set_up'; template_id?: string; recipient: PreviewRecipient };

export interface SaveWorkflowBody {
  name: string;
  steps: StepCard[];
  reviewed_emails?: Record<string, string>;
  reviewed_catalogue?: CatalogueReviews;
  expected_revision: number;
  note?: string;
  as_draft?: boolean;
}

export interface RecentMatch {
  decided_at: string | null; subject: string; claim_reference: string | null; followed_entry_id: string | null;
  gate_ok: boolean; reason: string | null; rule_key: string | null;
  switch_mode?: 'watching' | 'on';
  decision_id?: string; policy_version?: string; source?: ReplacementSource | null;
  workflow?: { id: string; name: string; version: number } | null; plan?: FrozenPlan | null;
  eligible?: boolean; readiness_issues?: string[]; run_id?: string | null; run?: RunSummary | null;
  ending?: string | null; disposition?: string; predictions?: Predictions | null;
}
export interface Matches { items: RecentMatch[]; followed_last_7_days: number; built_in: BuiltInMatch | null }

export interface HistoryItem {
  kind: string;
  at: string | null;
  by: string | null;
  note: string | null;
  mode?: AiMode;
  from_limit?: number | null;
  to_limit?: number | null;
  details?: Record<string, unknown>;
}

export interface Control { workflows_paused: boolean; revision: number; changed_by: string | null; changed_at: string | null }

export interface ExampleLinkWorkflow { workflow_id: string; name: string; state: WorkflowState; steps: StepCard[] | null }
export interface ExampleLinks {
  links: Record<string, { workflow_id: string | null; revision: number }>;
  workflows: ExampleLinkWorkflow[];
  seeds_by_action: Record<string, string>;
}

export interface SeedCounts { seeds: number; examples: number; rules: number }
export interface SeedItem {
  seed_key: string; name: string; status: 'exists' | 'create' | 'blocked'; reason: string | null;
  workflow_id: string | null; examples: number; rule: boolean; template_ids?: string[];
}
export interface SeedPlan { items: SeedItem[]; counts: SeedCounts }
export type SeedResult = SeedPlan;

export interface EffectCapacity {
  slot_limit: number; revision: number; occupied: number;
  min_limit?: number; max_limit?: number; default_limit?: number;
  active_runs?: number; unresolved_runs?: number; occupied_children?: number; legacy_slot_limit?: number;
}

// The repository still carries the obsolete @types/axios request type, which omits Axios 1.x
// AbortSignal support. Runtime axios is 1.9.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const withSignal = (config: Record<string, unknown>) => config as any;

export const getAiSwitch = async (signal?: AbortSignal): Promise<AiSwitch> =>
  (await client.get<AiSwitch>(`${base}/switch`, withSignal({ signal }))).data;

export const setAiSwitch = async (body: { mode: AiMode; expected_epoch: number; note?: string }): Promise<AiSwitch> =>
  (await client.post<AiSwitch>(`${base}/switch`, body)).data;

export const getOverview = async (days: number, signal?: AbortSignal, scope: EvidenceScope = 'caf-workflows-1'): Promise<Overview> =>
  (await client.get<Overview>(`${base}/overview`, withSignal({ params: { days, scope }, signal }))).data;

export const getSwitchHistory = async (): Promise<{ items: HistoryItem[] }> =>
  (await client.get<{ items: HistoryItem[] }>(`${base}/history`)).data;

export const pauseAllWorkflows = async (body: { expected_revision: number; note?: string }): Promise<Control> =>
  (await client.post<Control>(`${base}/workflows/pause-all`, body)).data;

export const resumeAllWorkflows = async (body: { expected_revision: number; note?: string }): Promise<Control> =>
  (await client.post<Control>(`${base}/workflows/resume-all`, body)).data;

export const listAutomationWorkflows = async (days: number, signal?: AbortSignal, scope: EvidenceScope = 'caf-workflows-1'): Promise<WorkflowList> =>
  (await client.get<WorkflowList>(`${base}/workflows`, withSignal({ params: { days, scope }, signal }))).data;

export const getStepOptions = async (): Promise<StepOptions> =>
  (await client.get<StepOptions>(`${base}/workflows/options`)).data;

const workflowPath = (id: string) => `${base}/workflows/${encodeURIComponent(id)}`;

export const getAutomationWorkflow = async (id: string, scope: EvidenceScope = 'caf-workflows-1'): Promise<WorkflowView> =>
  (await client.get<WorkflowView>(workflowPath(id), { params: { scope } })).data;
export const getActivationReview = async (id: string): Promise<ActivationReview> =>
  (await client.get<ActivationReview>(`${workflowPath(id)}/activation-review`)).data;
export const approveWorkflowVersion = async (id: string, body: { version: number; expected_revision: number; note?: string }): Promise<WorkflowView> =>
  (await client.post<WorkflowView>(`${workflowPath(id)}/approve`, body)).data;

export const createAutomationWorkflow = async (body: SaveWorkflowBody): Promise<WorkflowView> =>
  (await client.post<WorkflowView>(`${base}/workflows`, body)).data;

export const updateAutomationWorkflow = async (id: string, body: SaveWorkflowBody): Promise<WorkflowView> =>
  (await client.put<WorkflowView>(workflowPath(id), body)).data;

export const previewWorkflowSteps = async (steps: StepCard[]): Promise<{ items: PreviewItem[] }> =>
  (await client.post<{ items: PreviewItem[] }>(`${base}/workflows/preview`, { steps })).data;

export const setWorkflowState = async (
  id: string, body: { to: 'watching' | 'on' | 'paused'; expected_revision: number; version?: number; note?: string },
): Promise<WorkflowView> => (await client.post<WorkflowView>(`${workflowPath(id)}/state`, body)).data;

export const duplicateAutomationWorkflow = async (id: string): Promise<WorkflowView> =>
  (await client.post<WorkflowView>(`${workflowPath(id)}/duplicate`, {})).data;

export const deleteAutomationWorkflow = async (
  id: string, body: { expected_revision: number; note?: string },
): Promise<{ deleted: boolean; examples: number }> =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (await client.delete<{ deleted: boolean; examples: number }>(workflowPath(id), { data: body } as any)).data;

export const getAutomationWorkflowHistory = async (id: string): Promise<{ items: HistoryItem[] }> =>
  (await client.get<{ items: HistoryItem[] }>(`${workflowPath(id)}/history`)).data;

export const getWorkflowMatches = async (id: string, scope: EvidenceScope = 'caf-workflows-1'): Promise<Matches> =>
  (await client.get<Matches>(`${workflowPath(id)}/matches`, { params: { scope } })).data;

export interface DecisionComparison { scope: EvidenceScope; watch: RecentMatch | null; live: RecentMatch | null;
  changed_fields: Array<{ field: string; before: unknown; after: unknown }>;
  current_eligibility: { eligible_waiting: boolean; execution: false; assessed_at: string } | null }
export const getDecisionComparison = async (id: string, scope: EvidenceScope = 'caf-workflows-1'): Promise<DecisionComparison> =>
  (await client.get<DecisionComparison>(`${base}/decisions/${encodeURIComponent(id)}/comparison`, { params: { scope } })).data;
export const listReplacementRuns = async (params: { workflow?: string; status?: string; cursor?: string; limit?: number } = {}, attention = false): Promise<{ items: RunSummary[]; next_cursor: string | null }> =>
  (await client.get<{ items: RunSummary[]; next_cursor: string | null }>(`${base}/${attention ? 'attention' : 'runs'}`, { params })).data;
export const getReplacementRun = async (id: string): Promise<RunDetail> =>
  (await client.get<RunDetail>(`${base}/runs/${encodeURIComponent(id)}`)).data;
export const controlReplacementRun = async (id: string, action: 'pause' | 'resume' | 'check-sent' | 'send-again' | 'dealt-with', body: { expected_revision: number; note?: string }): Promise<RunDetail> =>
  (await client.post<RunDetail>(`${base}/runs/${encodeURIComponent(id)}/${action}`, body)).data;
export const recordReplacementContact = async (id: string, body: { received_at: string; channel: string; idempotency_key: string; note?: string }): Promise<RunDetail> =>
  (await client.post<RunDetail>(`${base}/runs/${encodeURIComponent(id)}/contact`, body)).data;

export const getExampleLinks = async (signal?: AbortSignal): Promise<ExampleLinks> =>
  (await client.get<ExampleLinks>(`${base}/example-links`, withSignal({ signal }))).data;

export const setExampleWorkflow = async (
  entryId: string, body: { workflow_id: string | null; sender: string; expected_revision: number; note?: string },
): Promise<{ entry_id: string; workflow_id: string | null; revision: number }> =>
  (await client.put<{ entry_id: string; workflow_id: string | null; revision: number }>(
    `${base}/examples/${encodeURIComponent(entryId)}/workflow`, body)).data;

export const getSeedPlan = async (): Promise<SeedPlan> => (await client.get<SeedPlan>(`${base}/seeds`)).data;

export const applySeeds = async (body: { expected_counts: SeedCounts; note?: string }): Promise<SeedResult> =>
  (await client.post<SeedResult>(`${base}/seeds`, body)).data;

const capacity = '/api/admin/caf-workflows/effect-capacity';

export const getEffectCapacity = async (): Promise<EffectCapacity> => (await client.get<EffectCapacity>(capacity)).data;

export const setEffectCapacity = async (
  body: { slot_limit: number; expected_revision: number; reason: string },
): Promise<EffectCapacity> => (await client.post<EffectCapacity>(capacity, body)).data;
